import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Stack,
  TextField,
  Typography,
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Checkbox,
  CircularProgress,
  Divider,
  Alert,
} from "@mui/material";
import dayjs from "dayjs";

import { getTransportGroupsByDate } from "../../api/transportApi.js";
import { createTransportGroup } from "../../api/transportBoardApi.js";

function normaliseTime(val) {
  if (!val) return null;
  const s = String(val);
  if (/^\d{2}:\d{2}:\d{2}$/.test(s)) return s.slice(0, 5);
  if (/^\d{2}:\d{2}$/.test(s)) return s;
  return null;
}

// Build a create payload that duplicates a bus's definition (route, capacity,
// journey type, provider/contact) into the target day. The copy starts fresh:
// Planned status, no passengers, no confirmation stamp.
function buildCopyPayload(bus, targetDate) {
  return {
    campDate: targetDate,
    groupName: bus.groupName || "Bus",
    capacity: Number(bus.capacity) || 0,
    campus: bus.campus ?? "",
    status: "Planned",
    meta: {
      journeyType: bus.journeyType || null,
      provider: bus.provider || null,
      contactName: bus.contactName || null,
      contactNumber: bus.contactNumber || null,
      clearFields: [],
    },
    stops: (Array.isArray(bus.stops) ? bus.stops : [])
      .slice()
      .sort((a, b) => (a.stopOrder ?? 0) - (b.stopOrder ?? 0))
      .map((s, i) => ({
        stopOrder: i + 1,
        location: String(s.location || "").trim() || null,
        campusId: s.campusId == null || s.campusId === "" ? null : Number(s.campusId),
        plannedTime: normaliseTime(s.plannedTime),
        stopType: s.stopType || null,
      })),
  };
}

function busDetail(bus) {
  const stops = Array.isArray(bus.stops) ? bus.stops : [];
  const route = stops
    .slice()
    .sort((a, b) => (a.stopOrder ?? 0) - (b.stopOrder ?? 0))
    .map((s) => s.location)
    .filter(Boolean)
    .join(" → ");
  return [
    bus.journeyType || null,
    bus.campus || null,
    route || null,
    `cap ${bus.capacity ?? "—"}`,
  ]
    .filter(Boolean)
    .join(" · ");
}

// Copy one or more buses from a chosen source day into `targetDate` (the day the
// board is showing). Each becomes a fresh Planned bus — route/capacity/contact
// are duplicated; passengers and confirmation are not.
export default function CopyBusDialog({ open, onClose, targetDate, onCopied }) {
  const defaultSourceDate = useMemo(
    () =>
      targetDate ? dayjs(targetDate).subtract(1, "day").format("YYYY-MM-DD") : "",
    [targetDate]
  );

  const [sourceDate, setSourceDate] = useState(defaultSourceDate);
  const [buses, setBuses] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [selectedIds, setSelectedIds] = useState(() => new Set());
  const [copying, setCopying] = useState(false);
  const [result, setResult] = useState(null);

  useEffect(() => {
    if (open) {
      setSourceDate(defaultSourceDate);
      setResult(null);
    }
  }, [open, defaultSourceDate]);

  const loadBuses = useCallback(async (date) => {
    if (!date) {
      setBuses([]);
      setSelectedIds(new Set());
      return;
    }
    setLoading(true);
    setError("");
    try {
      const data = await getTransportGroupsByDate(date);
      setBuses(Array.isArray(data) ? data : []);
      setSelectedIds(new Set()); // pick the specific bus(es) to copy
    } catch (e) {
      setError(e?.message || "Failed to load buses");
      setBuses([]);
      setSelectedIds(new Set());
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (open && sourceDate) loadBuses(sourceDate);
  }, [open, sourceDate, loadBuses]);

  const toggle = (id) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const handleCopy = async () => {
    if (!selectedIds.size || !targetDate) return;

    setCopying(true);
    setResult(null);

    const toCopy = buses.filter((b) => selectedIds.has(b.id));
    const outcomes = await Promise.allSettled(
      toCopy.map((bus) => createTransportGroup(buildCopyPayload(bus, targetDate)))
    );

    const created = outcomes.filter((o) => o.status === "fulfilled").length;
    const failed = outcomes.length - created;

    let message = `Copied ${created} bus${created === 1 ? "" : "es"}`;
    if (failed) message += ` · ${failed} failed`;
    setResult({ message, severity: failed ? "warning" : "success" });

    setCopying(false);
    await onCopied?.();
  };

  const targetLabel = targetDate
    ? dayjs(targetDate).format("ddd D MMM YYYY")
    : "this day";

  return (
    <Dialog open={open} onClose={copying ? undefined : onClose} fullWidth maxWidth="sm">
      <DialogTitle>Copy buses into {targetLabel}</DialogTitle>

      <DialogContent dividers>
        <Stack spacing={2}>
          <TextField
            label="Copy from"
            type="date"
            size="small"
            value={sourceDate}
            onChange={(e) => setSourceDate(e.target.value)}
            InputLabelProps={{ shrink: true }}
          />

          <Typography variant="caption" sx={{ opacity: 0.7 }}>
            Copies the route, capacity, journey type and contact. The new bus
            starts as Planned with no passengers.
          </Typography>

          <Divider />

          {loading ? (
            <Stack direction="row" spacing={1} alignItems="center">
              <CircularProgress size={18} />
              <Typography variant="body2">Loading buses…</Typography>
            </Stack>
          ) : error ? (
            <Alert severity="error">{error}</Alert>
          ) : buses.length === 0 ? (
            <Typography variant="body2" sx={{ opacity: 0.7 }}>
              No buses on the selected day.
            </Typography>
          ) : (
            <List dense disablePadding>
              {buses.map((bus) => (
                <ListItem key={bus.id} disableGutters disablePadding>
                  <ListItemButton onClick={() => toggle(bus.id)} sx={{ px: 0 }}>
                    <ListItemIcon sx={{ minWidth: 36 }}>
                      <Checkbox
                        edge="start"
                        size="small"
                        checked={selectedIds.has(bus.id)}
                        tabIndex={-1}
                        disableRipple
                      />
                    </ListItemIcon>
                    <ListItemText
                      primary={bus.groupName || "Unnamed bus"}
                      secondary={busDetail(bus)}
                    />
                  </ListItemButton>
                </ListItem>
              ))}
            </List>
          )}

          {result && <Alert severity={result.severity}>{result.message}</Alert>}
        </Stack>
      </DialogContent>

      <DialogActions>
        <Button onClick={onClose} disabled={copying}>
          Close
        </Button>
        <Button
          variant="contained"
          onClick={handleCopy}
          disabled={copying || loading || selectedIds.size === 0}
        >
          {copying
            ? "Copying…"
            : `Copy ${selectedIds.size} bus${selectedIds.size === 1 ? "" : "es"}`}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
